import React, { useState } from 'react'
import {
  MapPin, TrendingUp, ChevronDown, ChevronUp, Calculator,
  Lightbulb, ArrowRight, ArrowLeftRight, CheckCircle, XCircle,
  Briefcase, Star, Trophy, ChevronRight, Zap, Globe
} from 'lucide-react'

// ── 직업 스토리 시나리오 데이터 ───────────────────────────────────────────────
const SCENARIOS = [
  {
    id: 'driver',
    emoji: '🚕',
    title: '택시 기사·물류 배송원',
    category: '운송·물류',
    color: 'from-blue-400 to-sky-500',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    badge: 'bg-blue-100 text-blue-700',
    story: `배송 기사 준석씨는 시속 60km로 고속도로를 달립니다.
배송 거리와 시간을 계산해야 고객에게 도착 시간을 알릴 수 있습니다.
"속도×시간=거리니까, y=60x 정비례 관계를 쓰면 돼!"
x시간 후 이동 거리 y(km)= 60x 로 정확히 예측할 수 있었습니다.`,
    situation: '속도 60km/h 고정. 시간 x(h)와 거리 y(km) → y = 60x (정비례)',
    question: '2.5시간 후 이동 거리는 몇 km인가요?',
    xLabel: '시간 x (h)',
    yLabel: '거리 y (km)',
    slope: 60,
    intercept: 0,
    isInverse: false,
    slopeDesc: '시간 1시간 증가 → 거리 60km 증가 (일정 비율)',
    interceptDesc: '시간 0이면 거리 0 (원점 통과 직선)',
    equation: 'y = 60x',
    answer: 'y = 60 × 2.5 = 150 (km)',
    whyMath: '정비례 y=ax는 속도·단가·농도 등 일정한 비율로 증가하는 모든 상황에 쓰입니다.',
    insight: '배송 기사·택시 기사·항공 관제사는 정비례 관계로 도착 시간을 정확하게 예측합니다.',
    realAnswer: 150,
    unit: 'km',
    tableData: [
      { x: 1, y: 60 }, { x: 2, y: 120 }, { x: 3, y: 180 },
      { x: 4, y: 240 }, { x: 5, y: 300 },
    ],
    quiz: [
      { q: '시속 80km로 3시간 달리면 몇 km?', a: '240km', hint: 'y=80×3=240' },
      { q: '240km를 시속 60km로 가면 몇 시간?', a: '4시간', hint: '60x=240 → x=4' },
    ],
    jobs: ['택시 기사', '물류 배송원', '항공 관제사', '철도 기관사'],
  },
  {
    id: 'barista',
    emoji: '☕',
    title: '카페 바리스타',
    category: '식음료',
    color: 'from-amber-400 to-orange-500',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    badge: 'bg-amber-100 text-amber-700',
    story: `카페 바리스타 지은씨는 원두를 100g에 2,500원에 판매합니다.
고객이 원두 x(100g 단위)를 살 때 금액 y(원)를 빠르게 계산해야 합니다.
"y=2500x, 정비례 관계! x 단위가 늘어날수록 금액도 비례해서 늘어나지."
포스기에 수식을 입력해 자동 계산 시스템을 만들었습니다.`,
    situation: '원두 100g당 2,500원. 구매량 x(100g)와 금액 y(원) → y = 2500x',
    question: '원두 7단위(700g)를 구매하면 금액은?',
    xLabel: '구매량 x (100g)',
    yLabel: '금액 y (원)',
    slope: 2500,
    intercept: 0,
    isInverse: false,
    slopeDesc: '100g 1단위 증가 → 금액 2,500원 증가 (단가 일정)',
    interceptDesc: '구매량 0이면 금액 0 (원점 통과 직선)',
    equation: 'y = 2500x',
    answer: 'y = 2500 × 7 = 17,500 (원)',
    whyMath: '단가×수량=금액은 가장 흔한 정비례 활용입니다. 모든 가격 계산에 y=ax가 숨어 있습니다.',
    insight: '카페 바리스타·마트 계산원·회계사는 정비례 관계로 빠르게 가격과 금액을 계산합니다.',
    realAnswer: 17500,
    unit: '원',
    tableData: [
      { x: 1, y: 2500 }, { x: 2, y: 5000 }, { x: 3, y: 7500 },
      { x: 4, y: 10000 }, { x: 5, y: 12500 },
    ],
    quiz: [
      { q: '원두 5단위 구매 금액은?', a: '12,500원', hint: '2500×5=12500' },
      { q: '15,000원으로 살 수 있는 단위 수는?', a: '6단위', hint: '2500x=15000 → x=6' },
    ],
    jobs: ['카페 바리스타', '마트 계산원', '회계사', '영업 담당자'],
  },
  {
    id: 'engineer',
    emoji: '⚙️',
    title: '기계 엔지니어 (기어 설계)',
    category: '제조·엔지니어링',
    color: 'from-violet-400 to-purple-500',
    bg: 'bg-violet-50',
    border: 'border-violet-200',
    badge: 'bg-violet-100 text-violet-700',
    story: `기계 엔지니어 민준씨는 두 기어를 설계합니다.
기어 A의 잇수가 많을수록 회전수가 줄어드는 반비례 관계를 활용합니다.
"잇수×회전수=일정! y=360/x 반비례야."
기어 A 잇수 x와 분당 회전수 y를 y=360/x로 모델링해 최적 기어비를 결정했습니다.`,
    situation: '기어 잇수 x와 분당 회전수 y → xy = 360 (일정) → y = 360/x (반비례)',
    question: '기어 잇수가 24개일 때 분당 회전수는?',
    xLabel: '잇수 x (개)',
    yLabel: '회전수 y (회전/분)',
    slope: 360,
    intercept: 0,
    isInverse: true,
    slopeDesc: '잇수 x가 2배 → 회전수 y는 1/2배 (반비례)',
    interceptDesc: 'xy = 360 일정. x↑이면 y↓ (쌍곡선)',
    equation: 'y = 360/x',
    answer: 'y = 360 ÷ 24 = 15 (회전/분)',
    whyMath: '반비례 y=a/x는 기어·도르래·지레 등 역관계가 있는 기계장치 설계의 핵심입니다.',
    insight: '기계 엔지니어·자동차 기술자·로봇 개발자는 반비례로 기어비와 회전수를 설계합니다.',
    realAnswer: 15,
    unit: '회전/분',
    tableData: [
      { x: 6, y: 60 }, { x: 9, y: 40 }, { x: 12, y: 30 },
      { x: 18, y: 20 }, { x: 24, y: 15 }, { x: 36, y: 10 },
    ],
    quiz: [
      { q: '잇수 18개일 때 회전수는?', a: '20회전/분', hint: '360÷18=20' },
      { q: '회전수를 12로 하려면 잇수는?', a: '30개', hint: '360/x=12 → x=30' },
    ],
    jobs: ['기계 엔지니어', '자동차 기술자', '로봇 개발자', '항공 엔지니어'],
  },
  {
    id: 'nurse',
    emoji: '💉',
    title: '간호사·약사 (약물 농도)',
    category: '의료·보건',
    color: 'from-rose-400 to-pink-500',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    badge: 'bg-rose-100 text-rose-700',
    story: `간호사 수민씨는 약물 360mg을 물에 희석합니다.
물의 양(x mL)을 늘릴수록 농도(y mg/mL)는 낮아지는 반비례 관계입니다.
"농도=약물량÷부피, 즉 y=360/x!"
물을 90mL 넣으면 농도는 4mg/mL 로 정확히 처방 농도에 맞출 수 있었습니다.`,
    situation: '약물 360mg 고정. 물의 양 x(mL)와 농도 y(mg/mL) → y = 360/x (반비례)',
    question: '물을 120mL 넣으면 약물 농도는?',
    xLabel: '물의 양 x (mL)',
    yLabel: '농도 y (mg/mL)',
    slope: 360,
    intercept: 0,
    isInverse: true,
    slopeDesc: '물 양 2배 → 농도 1/2배 (반비례)',
    interceptDesc: 'xy = 360 일정. 물↑이면 농도↓ (쌍곡선)',
    equation: 'y = 360/x',
    answer: 'y = 360 ÷ 120 = 3 (mg/mL)',
    whyMath: '반비례는 농도·압력·전류 등 총량이 고정된 채로 양이 변하는 의학·과학 상황에 광범위하게 쓰입니다.',
    insight: '간호사·약사·화학자는 반비례 관계로 정확한 약물 농도와 희석 비율을 계산합니다.',
    realAnswer: 3,
    unit: 'mg/mL',
    tableData: [
      { x: 30, y: 12 }, { x: 60, y: 6 }, { x: 90, y: 4 },
      { x: 120, y: 3 }, { x: 180, y: 2 }, { x: 360, y: 1 },
    ],
    quiz: [
      { q: '물 90mL 넣을 때 농도는?', a: '4 mg/mL', hint: '360÷90=4' },
      { q: '농도를 2mg/mL로 만들려면 물 몇 mL?', a: '180 mL', hint: '360/x=2 → x=180' },
    ],
    jobs: ['간호사', '약사', '화학자', '임상병리사'],
  },
  {
    id: 'interior',
    emoji: '🏠',
    title: '인테리어 디자이너',
    category: '건축·인테리어',
    color: 'from-teal-400 to-emerald-500',
    bg: 'bg-teal-50',
    border: 'border-teal-200',
    badge: 'bg-teal-100 text-teal-700',
    story: `인테리어 디자이너 하은씨는 36m² 방에 정사각형 타일을 깔려 합니다.
타일 한 변의 길이 x(m)와 필요한 타일 수 y(개)는 반비례 관계입니다.
"넓이=타일 하나 넓이×개수 → x²×y=36 → y=36/x²"
타일 크기를 바꿀 때마다 필요 개수를 빠르게 계산했습니다.`,
    situation: '방 넓이 36m². 타일 한 변 x(m), 타일 수 y(개) → x²×y=36 → y=36/x²',
    question: '한 변이 1m인 타일을 쓰면 몇 개 필요한가요?',
    xLabel: '타일 한 변 x (m)',
    yLabel: '타일 수 y (개)',
    slope: 36,
    intercept: 0,
    isInverse: true,
    slopeDesc: '타일 크기 2배 → 개수 1/4배 (반비례 변형)',
    interceptDesc: 'x²×y = 36 일정. 타일 클수록 개수 감소',
    equation: 'y = 36/x²',
    answer: 'y = 36 ÷ 1² = 36 (개)',
    whyMath: '반비례의 변형(y=a/x²)도 같은 논리입니다. 총량이 고정된 상황에서 단위가 커지면 수량이 줄어듭니다.',
    insight: '인테리어 디자이너·건축가·조경사는 반비례 관계로 재료 수량과 단가를 효율적으로 계산합니다.',
    realAnswer: 36,
    unit: '개',
    tableData: [
      { x: 1, y: 36 }, { x: 2, y: 9 }, { x: 3, y: 4 }, { x: 6, y: 1 },
    ],
    quiz: [
      { q: '한 변 2m 타일을 쓰면 몇 개?', a: '9개', hint: '36÷4=9' },
      { q: '한 변 3m 타일을 쓰면 몇 개?', a: '4개', hint: '36÷9=4' },
    ],
    jobs: ['인테리어 디자이너', '건축가', '조경사', '시공 관리자'],
  },
]

// ── 직업 탐색 데이터 ──────────────────────────────────────────────────────────
const CAREERS = [
  {
    icon: '🚕',
    title: '운송·물류 종사자',
    example: '속도×시간=거리 (정비례)로 도착 시간 예측',
    formula: 'y = ax (a: 속도)',
    salary: '연 3,000만~6,000만원',
    color: 'from-blue-500 to-sky-500',
  },
  {
    icon: '☕',
    title: '식음료·판매·회계사',
    example: '단가×수량=금액 (정비례)로 빠른 가격 계산',
    formula: 'y = ax (a: 단가)',
    salary: '연 3,000만~8,000만원',
    color: 'from-amber-500 to-orange-500',
  },
  {
    icon: '⚙️',
    title: '기계·자동차 엔지니어',
    example: '기어 잇수×회전수=일정 (반비례)로 기어비 설계',
    formula: 'y = a/x (a: 상수)',
    salary: '연 4,000만~1억원',
    color: 'from-violet-500 to-purple-500',
  },
  {
    icon: '💉',
    title: '간호사·약사·화학자',
    example: '약물 총량÷부피=농도 (반비례)로 처방 농도 관리',
    formula: 'y = a/x (a: 약물량)',
    salary: '연 4,000만~9,000만원',
    color: 'from-rose-500 to-pink-500',
  },
  {
    icon: '🏠',
    title: '인테리어·건축 디자이너',
    example: '타일 크기↑→개수↓ (반비례)로 자재 수량 계산',
    formula: 'y = a/x²',
    salary: '연 3,500만~8,000만원',
    color: 'from-teal-500 to-emerald-500',
  },
  {
    icon: '🔭',
    title: '물리학자·항공우주 공학자',
    example: '케플러 법칙(공전주기²∝거리³), 중력(반비례²)',
    formula: 'y = a/x²',
    salary: '연 5,000만~1.5억원',
    color: 'from-indigo-500 to-violet-500',
  },
]

// ── 도전 퀴즈 ─────────────────────────────────────────────────────────────────
const CHALLENGES = [
  {
    id: 'c1',
    emoji: '🚕',
    level: '⭐ 쉬움',
    levelColor: 'bg-emerald-100 text-emerald-700',
    question: '시속 90km로 달리는 버스가 있습니다. 4시간 30분 후 이동 거리를 구하고, y = ax 형태로 나타내세요.',
    hint: '4시간 30분 = 4.5시간. y = 90 × 4.5',
    answer: 'y = 90x, 이동 거리 = 90 × 4.5 = 405 km',
    explanation: 'y = 90x (정비례)\nx = 4.5를 대입하면 y = 90 × 4.5 = 405 km\n시간과 거리는 속도가 일정할 때 정비례합니다.',
  },
  {
    id: 'c2',
    emoji: '⚙️',
    level: '⭐⭐ 보통',
    levelColor: 'bg-amber-100 text-amber-700',
    question: '두 기어의 잇수×회전수 = 480 (일정)입니다. 잇수가 20개일 때와 30개일 때의 회전수를 각각 구하고, x=40일 때의 회전수도 구하세요.',
    hint: 'y = 480/x. 각각 x=20, 30, 40 대입',
    answer: 'x=20: 24회전/분, x=30: 16회전/분, x=40: 12회전/분',
    explanation: 'y = 480/x (반비례)\nx=20: y=480÷20=24\nx=30: y=480÷30=16\nx=40: y=480÷40=12\n잇수가 늘어날수록 회전수는 줄어드는 반비례 관계입니다.',
  },
  {
    id: 'c3',
    emoji: '💉',
    level: '⭐⭐ 보통',
    levelColor: 'bg-amber-100 text-amber-700',
    question: '약물 A 600mg을 희석합니다. 농도를 5mg/mL로 맞추려면 물이 몇 mL 필요한가요? 또 물 200mL에 희석하면 농도는?',
    hint: 'y = 600/x. 5 = 600/x → x=? 그리고 y = 600/200',
    answer: '물 120mL 필요. 200mL 희석 시 농도 3mg/mL',
    explanation: 'y = 600/x (반비례)\n① 5 = 600/x → x = 600÷5 = 120 mL\n② y = 600÷200 = 3 mg/mL\n약물 총량이 고정되면 물의 양과 농도는 반비례합니다.',
  },
  {
    id: 'c4',
    emoji: '📊',
    level: '⭐⭐⭐ 어려움',
    levelColor: 'bg-red-100 text-red-700',
    question: '표를 보고 정비례인지 반비례인지 판별하고, 관계식을 구하세요.\n[x: 2, 4, 6, 8 / y: 150, 75, 50, 37.5]',
    hint: 'x×y 값을 계산해보세요. 일정하면 반비례!',
    answer: '반비례. y = 300/x',
    explanation: 'x×y 확인:\n2×150=300, 4×75=300, 6×50=300, 8×37.5=300\nxy = 300 (일정) → 반비례!\n관계식: y = 300/x\n정비례 확인(y÷x)이 일정하지 않으므로 정비례가 아닙니다.',
  },
]

const CATEGORY_COLORS: Record<string, string> = {
  '운송·물류': 'bg-blue-100 text-blue-700',
  '식음료': 'bg-amber-100 text-amber-700',
  '제조·엔지니어링': 'bg-violet-100 text-violet-700',
  '의료·보건': 'bg-rose-100 text-rose-700',
  '건축·인테리어': 'bg-teal-100 text-teal-700',
}

// ── 계산기 컴포넌트 ───────────────────────────────────────────────────────────
type CalcMode = 'forward' | 'inverse'

interface CalcResult {
  mode: 'forward' | 'inverse'
  inputVal: number
  outputVal: number
  steps: string[]
}

function ScenarioCalculator({ scenario }: { scenario: typeof SCENARIOS[0] }) {
  const [mode, setMode] = useState<CalcMode>('forward')
  const [inputVal, setInputVal] = useState('')
  const [result, setResult] = useState<CalcResult | null>(null)
  const [userAnswer, setUserAnswer] = useState('')
  const [checked, setChecked] = useState<boolean | null>(null)

  const reset = () => { setInputVal(''); setResult(null); setUserAnswer(''); setChecked(null) }
  const handleModeChange = (m: CalcMode) => { setMode(m); reset() }

  const calculate = () => {
    const v = parseFloat(inputVal)
    if (isNaN(v) || v === 0) return

    if (scenario.isInverse) {
      // 반비례: y = a/x
      if (mode === 'forward') {
        const y = Math.round((scenario.slope / v) * 100) / 100
        setResult({
          mode: 'forward',
          inputVal: v,
          outputVal: y,
          steps: [
            `공식: ${scenario.equation}`,
            `x = ${v} 대입`,
            `y = ${scenario.slope} ÷ ${v}`,
            `y = ${y.toLocaleString()} ${scenario.unit}`,
          ],
        })
      } else {
        const x = Math.round((scenario.slope / v) * 100) / 100
        setResult({
          mode: 'inverse',
          inputVal: v,
          outputVal: x,
          steps: [
            `방정식: ${scenario.equation}`,
            `목표 y = ${v}으로 설정`,
            `${v} = ${scenario.slope} ÷ x`,
            `x = ${scenario.slope} ÷ ${v}`,
            `x = ${x.toLocaleString()} ${scenario.xLabel.split(' ')[1]?.replace(/[()]/g, '') || ''}`,
          ],
        })
      }
    } else {
      // 정비례: y = ax
      if (mode === 'forward') {
        const y = Math.round(scenario.slope * v * 100) / 100
        setResult({
          mode: 'forward',
          inputVal: v,
          outputVal: y,
          steps: [
            `공식: ${scenario.equation}`,
            `x = ${v} 대입`,
            `y = ${scenario.slope} × ${v}`,
            `y = ${y.toLocaleString()} ${scenario.unit}`,
          ],
        })
      } else {
        if (scenario.slope === 0) return
        const x = Math.round((v / scenario.slope) * 100) / 100
        setResult({
          mode: 'inverse',
          inputVal: v,
          outputVal: x,
          steps: [
            `방정식: ${scenario.equation}`,
            `목표 y = ${v.toLocaleString()}으로 설정`,
            `${scenario.slope}x = ${v.toLocaleString()}`,
            `x = ${v.toLocaleString()} ÷ ${scenario.slope}`,
            `x = ${x.toLocaleString()} ${scenario.xLabel.split(' ')[1]?.replace(/[()]/g, '') || ''}`,
          ],
        })
      }
    }
    setUserAnswer('')
    setChecked(null)
  }

  const checkAnswer = () => {
    if (!result) return
    const ua = parseFloat(userAnswer.replace(/,/g, ''))
    const tolerance = Math.abs(result.outputVal) * 0.02 + 1
    setChecked(Math.abs(ua - result.outputVal) <= tolerance)
  }

  const xLabelShort = scenario.xLabel.split(' ')[0]
  const yLabelShort = scenario.yLabel.split(' ')[0]
  const accentColor = scenario.isInverse ? 'violet' : 'blue'

  return (
    <div className="card border-2 border-sky-100">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <Calculator size={15} className="text-sky-600" />
          직접 계산해보기
        </p>
        <div className="flex rounded-lg overflow-hidden border border-slate-200 text-xs">
          <button
            onClick={() => handleModeChange('forward')}
            className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1 ${
              mode === 'forward' ? 'bg-sky-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowRight size={11} />
            {xLabelShort} → {yLabelShort}
          </button>
          <button
            onClick={() => handleModeChange('inverse')}
            className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1 ${
              mode === 'inverse' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowLeftRight size={11} />
            역산 (풀기)
          </button>
        </div>
      </div>

      <div className="mb-4 bg-slate-50 rounded-xl px-4 py-3 flex items-center gap-3">
        <span className="text-xs text-slate-400 font-medium">공식</span>
        <span className="font-mono font-bold text-sky-700 text-lg">{scenario.equation}</span>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${scenario.isInverse ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700'}`}>
          {scenario.isInverse ? '반비례' : '정비례'}
        </span>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="flex-1">
          <label className="text-xs font-semibold text-slate-500">
            {mode === 'forward'
              ? `🔢 ${scenario.xLabel} 를 입력하세요`
              : `🎯 목표 ${scenario.yLabel} 를 입력하세요`}
          </label>
          <input
            type="number"
            value={inputVal}
            onChange={e => { setInputVal(e.target.value); setResult(null); setChecked(null) }}
            onKeyDown={e => e.key === 'Enter' && calculate()}
            placeholder="숫자를 입력하고 계산!"
            className="w-full mt-1 text-base border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-sky-300 transition-all"
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={calculate}
            disabled={!inputVal}
            className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all ${
              mode === 'forward'
                ? 'bg-sky-600 hover:bg-sky-700 text-white disabled:bg-slate-200 disabled:text-slate-400'
                : 'bg-blue-600 hover:bg-blue-700 text-white disabled:bg-slate-200 disabled:text-slate-400'
            }`}
          >
            계산!
          </button>
        </div>
      </div>

      {result && (
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-xs font-bold text-amber-700 mb-3 flex items-center gap-1">
              📝 풀이 과정 (단계별)
            </p>
            <div className="space-y-1.5">
              {result.steps.map((step, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className={`text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    i === result.steps.length - 1 ? 'bg-sky-500 text-white' : 'bg-amber-200 text-amber-700'
                  }`}>{i + 1}</span>
                  <span className={`text-sm font-mono ${
                    i === result.steps.length - 1 ? 'font-black text-sky-700 text-base' : 'text-amber-800'
                  }`}>{step}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={`rounded-xl px-5 py-4 border-2 ${
            mode === 'forward' ? 'bg-sky-50 border-sky-300' : 'bg-blue-50 border-blue-300'
          }`}>
            <p className="text-xs font-semibold text-slate-500 mb-1">
              {mode === 'forward'
                ? `${scenario.xLabel} = ${result.inputVal} 일 때`
                : `목표 ${scenario.yLabel} = ${result.inputVal.toLocaleString()} ${scenario.unit} 일 때`}
            </p>
            <div className="flex items-baseline gap-2">
              <span className={`text-3xl font-black ${mode === 'forward' ? 'text-sky-700' : 'text-blue-700'}`}>
                {result.outputVal.toLocaleString()}
              </span>
              <span className="text-base font-semibold text-slate-500">
                {mode === 'forward' ? scenario.unit : scenario.xLabel.split(' ')[1]?.replace(/[()]/g, '') || ''}
              </span>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <p className="text-xs font-bold text-slate-600 mb-2">🧑‍🎓 답 맞춰보기</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={userAnswer}
                onChange={e => { setUserAnswer(e.target.value); setChecked(null) }}
                onKeyDown={e => e.key === 'Enter' && checkAnswer()}
                placeholder={`내 답: __ ${mode === 'forward' ? scenario.unit : ''}`}
                className={`flex-1 text-sm border-2 rounded-lg px-3 py-2 focus:outline-none transition-all ${
                  checked === null ? 'border-slate-200' : checked ? 'border-emerald-400 bg-emerald-50' : 'border-red-400 bg-red-50'
                }`}
              />
              <button
                onClick={checkAnswer}
                disabled={!userAnswer}
                className="px-4 py-2 rounded-lg bg-slate-700 text-white text-sm font-medium disabled:bg-slate-200 disabled:text-slate-400 hover:bg-slate-800 transition-colors"
              >
                확인
              </button>
            </div>
            {checked !== null && (
              <div className={`mt-2 flex items-center gap-2 text-sm font-semibold ${checked ? 'text-emerald-600' : 'text-red-600'}`}>
                {checked
                  ? <><CheckCircle size={16} /> 정답이에요! 🎉</>
                  : <><XCircle size={16} /> 풀이 과정을 다시 확인해보세요!</>}
              </div>
            )}
          </div>
          <button onClick={reset} className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
            ↺ 다시 입력하기
          </button>
        </div>
      )}
    </div>
  )
}

// ── 퀴즈 컴포넌트 ─────────────────────────────────────────────────────────────
function QuizSection({ quiz }: { quiz: typeof SCENARIOS[0]['quiz'] }) {
  const [revealed, setRevealed] = useState<Record<number, boolean>>({})
  const [showHint, setShowHint] = useState<Record<number, boolean>>({})

  return (
    <div className="space-y-3">
      {quiz.map((q, i) => (
        <div key={i} className="border border-slate-200 rounded-xl p-4 bg-white">
          <p className="text-sm font-semibold text-slate-800 mb-2">❓ {q.q}</p>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowHint(h => ({ ...h, [i]: !h[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
            >
              <Lightbulb size={11} /> 힌트
            </button>
            <button
              onClick={() => setRevealed(r => ({ ...r, [i]: !r[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-sky-100 text-sky-700 hover:bg-sky-200 transition-colors"
            >
              {revealed[i] ? '정답 숨기기' : '정답 보기'}
            </button>
          </div>
          {showHint[i] && (
            <div className="mt-2 bg-amber-50 rounded-lg px-3 py-2 text-xs text-amber-700">
              💡 힌트: <span className="font-mono">{q.hint}</span>
            </div>
          )}
          {revealed[i] && (
            <div className="mt-2 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2">
              <span className="text-sm font-bold text-sky-700">✅ 정답: {q.a}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ── 챌린지 카드 컴포넌트 ──────────────────────────────────────────────────────
function ChallengeCard({ ch }: { ch: typeof CHALLENGES[0] }) {
  const [showHint, setShowHint] = useState(false)
  const [revealed, setRevealed] = useState(false)

  return (
    <div className="card border border-slate-200">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{ch.emoji}</span>
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${ch.levelColor}`}>{ch.level}</span>
        </div>
      </div>
      <p className="text-sm text-slate-800 font-medium leading-relaxed mb-3 whitespace-pre-line">{ch.question}</p>
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setShowHint(h => !h)}
          className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
        >
          <Lightbulb size={11} /> 힌트
        </button>
        <button
          onClick={() => setRevealed(r => !r)}
          className="text-xs px-3 py-1.5 rounded-full bg-sky-100 text-sky-700 hover:bg-sky-200 transition-colors flex items-center gap-1"
        >
          <Trophy size={11} /> {revealed ? '정답 숨기기' : '정답 확인'}
        </button>
      </div>
      {showHint && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
          💡 {ch.hint}
        </div>
      )}
      {revealed && (
        <div className="mt-3 bg-sky-50 border border-sky-200 rounded-xl p-3 space-y-2">
          <p className="text-sm font-bold text-sky-700">✅ 정답: {ch.answer}</p>
          <p className="text-xs text-sky-600 leading-relaxed whitespace-pre-line">{ch.explanation}</p>
        </div>
      )}
    </div>
  )
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────
type Tab = 'scenarios' | 'careers' | 'challenges'

export default function F1_1RealWorldPage() {
  const [selected, setSelected] = useState(SCENARIOS[0])
  const [showTable, setShowTable] = useState(false)
  const [filterCat, setFilterCat] = useState<string>('전체')
  const [activeTab, setActiveTab] = useState<Tab>('scenarios')

  const categories = ['전체', ...Array.from(new Set(SCENARIOS.map(s => s.category)))]
  const filtered = filterCat === '전체' ? SCENARIOS : SCENARIOS.filter(s => s.category === filterCat)

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'scenarios', label: '시나리오 탐색', icon: <MapPin size={14} /> },
    { id: 'careers', label: '직업과 정비례·반비례', icon: <Briefcase size={14} /> },
    { id: 'challenges', label: '도전 문제', icon: <Trophy size={14} /> },
  ]

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-sky-600 flex items-center justify-center">
            <Globe size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">꿈꾸는 직업과 정비례·반비례</h1>
            <p className="text-sm text-slate-500">배송 기사, 바리스타, 엔지니어, 간호사, 디자이너 — 모두 정비례·반비례를 활용합니다!</p>
          </div>
        </div>

        <div className="mt-4 rounded-2xl bg-gradient-to-r from-blue-600 to-sky-600 text-white p-5">
          <p className="font-bold text-lg mb-1">🌟 꿈의 직업에서 정비례·반비례를 만나다!</p>
          <p className="text-sm opacity-90 mb-3">y=ax(정비례)와 y=a/x(반비례)가 세상 모든 속도·가격·기어·농도 계산의 기초입니다.</p>
          <div className="grid grid-cols-4 md:grid-cols-5 gap-2">
            {[
              { icon: '🚕', job: '배송 기사' },
              { icon: '☕', job: '카페 바리스타' },
              { icon: '⚙️', job: '기계 엔지니어' },
              { icon: '💉', job: '간호사·약사' },
              { icon: '🏠', job: '인테리어 디자이너' },
            ].map(({ icon, job }) => (
              <div key={job} className="bg-white/20 rounded-xl px-2 py-2 text-center">
                <p className="text-2xl">{icon}</p>
                <p className="text-xs font-bold mt-1">{job}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-slate-100 p-1 rounded-xl w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-white text-blue-700 shadow-sm'
                : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── TAB 1: 시나리오 ── */}
      {activeTab === 'scenarios' && (
        <>
          <div className="flex gap-2 mb-4 flex-wrap">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setFilterCat(cat)}
                className={`text-xs px-4 py-2 rounded-full font-medium transition-colors ${
                  filterCat === cat
                    ? 'bg-blue-600 text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* 좌측: 시나리오 목록 */}
            <div className="space-y-3">
              {filtered.map(scenario => (
                <button
                  key={scenario.id}
                  onClick={() => { setSelected(scenario); setShowTable(false) }}
                  className={`w-full text-left rounded-2xl border-2 p-4 transition-all ${
                    selected.id === scenario.id
                      ? `${scenario.border} ${scenario.bg} shadow-md`
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{scenario.emoji}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-slate-800">{scenario.title}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[scenario.category]}`}>
                          {scenario.category}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{scenario.situation}</p>
                      <div className="mt-1.5 font-mono text-xs text-blue-600 font-semibold">{scenario.equation}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* 우측: 시나리오 상세 */}
            <div className="lg:col-span-2 space-y-4">
              <div className={`rounded-2xl border-2 ${selected.border} ${selected.bg} p-5`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-4xl">{selected.emoji}</span>
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">{selected.title}</h2>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[selected.category]}`}>
                        {selected.category}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${selected.isInverse ? 'bg-violet-100 text-violet-700' : 'bg-blue-100 text-blue-700'}`}>
                        {selected.isInverse ? '반비례' : '정비례'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mb-4 bg-white/70 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-bold text-slate-500 mb-2">📖 이야기로 만나는 정비례·반비례</p>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{selected.story}</p>
                </div>

                <p className="text-sm font-semibold text-slate-600 mb-1">📌 수학적 상황</p>
                <p className="text-sm text-slate-700 leading-relaxed mb-3">{selected.situation}</p>

                <div className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 mb-3">📐 관계식으로 나타내기</p>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-2xl font-black text-blue-700 font-mono">{selected.equation}</span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-2.5">
                      <p className="text-xs text-orange-500 font-semibold">변화 설명</p>
                      <p className="text-xs text-orange-700 mt-0.5">{selected.slopeDesc}</p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
                      <p className="text-xs text-blue-500 font-semibold">그래프 특징</p>
                      <p className="text-xs text-blue-700 mt-0.5">{selected.interceptDesc}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-2">💬 문제</p>
                <p className="text-base text-slate-800 font-medium">{selected.question}</p>
                <div className="mt-3 bg-sky-50 border border-sky-200 rounded-xl px-4 py-3">
                  <p className="text-sm font-bold text-sky-700 whitespace-pre-line">✅ 풀이: {selected.answer}</p>
                </div>
                <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                  <p className="text-xs text-blue-700">
                    <span className="font-semibold">왜 정비례·반비례인가?</span> {selected.whyMath}
                  </p>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <TrendingUp size={15} className="text-blue-500" /> 표로 보기
                  </p>
                  <button
                    onClick={() => setShowTable(!showTable)}
                    className="text-xs flex items-center gap-1 text-slate-500 hover:text-slate-700 bg-slate-100 px-3 py-1 rounded-full"
                  >
                    {showTable ? '표 접기' : '표 펼치기'}
                    {showTable ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                </div>
                {showTable && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr>
                          <th className="border border-slate-200 px-3 py-2 bg-orange-50 text-orange-700 text-left">{selected.xLabel}</th>
                          <th className="border border-slate-200 px-3 py-2 bg-sky-50 text-sky-700 text-left">{selected.yLabel}</th>
                          <th className="border border-slate-200 px-3 py-2 bg-slate-50 text-slate-600 text-left">
                            {selected.isInverse ? 'xy 곱 (일정?)' : 'y/x 비 (일정?)'}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {selected.tableData.map((row, i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="border border-slate-200 px-3 py-2 font-mono font-bold text-orange-600">{row.x}</td>
                            <td className="border border-slate-200 px-3 py-2 font-mono font-bold text-sky-700">{row.y.toLocaleString()}</td>
                            <td className="border border-slate-200 px-3 py-2 text-xs text-slate-500 font-mono text-blue-600">
                              {selected.isInverse
                                ? (row.x * row.y).toLocaleString()
                                : (row.y / row.x).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {!showTable && (
                  <div className="bg-slate-50 rounded-xl p-4 text-center text-slate-500 text-sm">
                    <p className="text-2xl mb-1">📋</p>
                    <p>표 펼치기를 눌러 값의 변화를 확인해보세요!</p>
                  </div>
                )}
              </div>

              <ScenarioCalculator scenario={selected} />

              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-3">🧩 연습 문제</p>
                <QuizSection quiz={selected.quiz} />
              </div>

              <div className="card bg-slate-50 border border-slate-200">
                <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Briefcase size={14} className="text-blue-500" /> 이 수학을 쓰는 직업
                </p>
                <div className="flex flex-wrap gap-2">
                  {selected.jobs.map(job => (
                    <span key={job} className="text-xs bg-blue-100 text-blue-700 px-3 py-1.5 rounded-full font-medium flex items-center gap-1">
                      <Star size={10} /> {job}
                    </span>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl bg-gradient-to-r from-slate-800 to-slate-700 text-white p-5">
                <p className="text-sm font-bold mb-1 flex items-center gap-2">
                  <TrendingUp size={15} /> 실제로 어디서 쓰이나요?
                </p>
                <p className="text-sm text-slate-300 leading-relaxed">{selected.insight}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {['정비례', '반비례', 'y=ax', 'y=a/x', '원점직선', '쌍곡선'].map(tag => (
                    <span key={tag} className="text-xs bg-white/10 px-2 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 card bg-gradient-to-br from-blue-50 to-sky-50 border-blue-200">
            <h3 className="font-bold text-slate-800 mb-4 text-base">📌 정비례·반비례 핵심 공식</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { icon: '📈', title: '정비례 y = ax', desc: '• y÷x = a (일정)\n• x↑ → y↑ (같은 방향)\n• 그래프: 원점 통과 직선\n• a>0: 1·3사분면 / a<0: 2·4사분면', color: 'text-blue-600' },
                { icon: '📉', title: '반비례 y = a/x', desc: '• xy = a (일정)\n• x↑ → y↓ (반대 방향)\n• 그래프: 쌍곡선\n• a>0: 1·3사분면 / a<0: 2·4사분면', color: 'text-violet-600' },
                { icon: '🔍', title: '판별 꿀팁', desc: '① y÷x 일정 → 정비례\n② xy 일정 → 반비례\n③ x↑→y↑ → 정비례\n④ x↑→y↓ → 반비례', color: 'text-amber-600' },
              ].map(({ icon, title, desc, color }) => (
                <div key={title} className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-2xl mb-2">{icon}</p>
                  <p className={`font-bold text-sm ${color}`}>{title}</p>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed whitespace-pre-line">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── TAB 2: 직업 탐색 ── */}
      {activeTab === 'careers' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-blue-600 to-sky-600 text-white p-6">
            <h2 className="text-xl font-bold mb-2">💼 정비례·반비례를 사용하는 직업들</h2>
            <p className="text-sm text-blue-100">
              y=ax(정비례)와 y=a/x(반비례)는 운송·판매·기계·의료·건축 등 수많은 직업의 핵심 계산 도구입니다!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
            {CAREERS.map(career => (
              <div key={career.title} className="card border border-slate-200 hover:shadow-md transition-shadow">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${career.color} flex items-center justify-center text-2xl mb-3`}>
                  {career.icon}
                </div>
                <h3 className="font-bold text-slate-800 mb-1">{career.title}</h3>
                <p className="text-xs text-slate-500 mb-3 leading-relaxed">{career.example}</p>
                <div className="bg-slate-50 rounded-lg p-3 mb-3">
                  <p className="text-xs text-slate-400 mb-1">실제 사용 공식</p>
                  <code className="text-xs font-mono text-blue-700 font-bold">{career.formula}</code>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                    💰 {career.salary}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="card border border-slate-200 bg-gradient-to-br from-slate-800 to-slate-900 text-white mb-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Zap size={20} className="text-yellow-400" /> AI 시대에도 정비례·반비례가 필요한 이유
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { title: '데이터 분석·AI 모델', desc: '데이터 간 비례 관계 파악이 머신러닝 회귀분석의 출발점. y=ax 직선이 기초!', color: 'border-yellow-400' },
                { title: '물류·스마트 배송', desc: '속도·시간·거리 정비례 계산으로 실시간 도착 예측 시스템을 만듭니다.', color: 'border-sky-400' },
                { title: '의약·바이오테크', desc: '약물 농도, 희석 비율 등 반비례 관계가 정밀 의료의 핵심입니다.', color: 'border-emerald-400' },
                { title: '로봇·자동화 공학', desc: '기어비·토크·회전수의 반비례로 로봇 팔과 자동차 변속기를 설계합니다.', color: 'border-purple-400' },
              ].map(item => (
                <div key={item.title} className={`border-l-4 ${item.color} pl-4 py-2`}>
                  <p className="font-semibold text-sm mb-1">{item.title}</p>
                  <p className="text-xs text-slate-300 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="card border border-blue-200 bg-blue-50">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <ChevronRight size={18} className="text-blue-600" /> 정비례·반비례에서 시작하는 학습 경로
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              {[
                { step: '중1', topic: '수와 연산', active: false },
                { step: '중1', topic: '문자와 식', active: false },
                { step: '중1', topic: '정비례·반비례', active: true },
                { step: '중2', topic: '일차함수', active: false },
                { step: '중3', topic: '이차함수', active: false },
                { step: '고1', topic: '함수', active: false },
                { step: '직업', topic: '데이터 분석', active: false },
              ].map((item, i) => (
                <React.Fragment key={`${item.step}-${item.topic}`}>
                  <div className={`rounded-xl px-3 py-2 text-center ${item.active ? 'bg-blue-600 text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>
                    <p className="text-xs font-bold">{item.step}</p>
                    <p className="text-xs mt-0.5">{item.topic}</p>
                  </div>
                  {i < 6 && <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── TAB 3: 도전 문제 ── */}
      {activeTab === 'challenges' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-blue-500 to-sky-600 text-white p-6">
            <h2 className="text-xl font-bold mb-2">🏆 실생활 도전 문제</h2>
            <p className="text-sm text-blue-100">
              실제 상황에서 정비례·반비례 관계를 직접 찾고 계산해보세요!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
            {CHALLENGES.map(ch => (
              <ChallengeCard key={ch.id} ch={ch} />
            ))}
          </div>

          <div className="card border border-slate-200">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Star size={16} className="text-blue-500" /> 정비례·반비례 핵심 공식 정리
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { step: '① 정비례 y = ax', desc: 'y/x = a 일정\nx↑ → y↑ (같은 방향)\n원점 통과 직선\na>0: 우상향 / a<0: 우하향', example: 'y=60x (속도×시간)', color: 'bg-blue-50 border-blue-200 text-blue-800' },
                { step: '② 반비례 y = a/x', desc: 'xy = a 일정\nx↑ → y↓ (반대 방향)\n쌍곡선 그래프\n원점·축 통과 안 함', example: 'y=360/x (기어 설계)', color: 'bg-violet-50 border-violet-200 text-violet-800' },
                { step: '③ 판별 & 활용', desc: '① y/x 일정 → 정비례\n② xy 일정 → 반비례\n③ 실생활: 총량 고정=반비례\n④ 단가×수량=정비례', example: 'x↑y↑ vs x↑y↓', color: 'bg-amber-50 border-amber-200 text-amber-800' },
              ].map(item => (
                <div key={item.step} className={`border rounded-xl p-4 ${item.color}`}>
                  <p className="font-bold text-sm mb-2">{item.step}</p>
                  <p className="text-xs leading-relaxed mb-2 whitespace-pre-line">{item.desc}</p>
                  <div className="bg-white/60 rounded-lg px-2 py-1">
                    <p className="text-xs font-mono font-bold">{item.example}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
              <p className="text-sm font-bold text-blue-700">정비례·반비례 → 일차함수 → 이차함수 → 데이터 분석!</p>
              <p className="text-xs text-blue-500 mt-1">두 양의 관계를 식으로 나타내는 능력이 모든 과학·공학·경제의 출발점입니다!</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
